import { createSelector } from '@reduxjs/toolkit';
import { memoize } from 'lodash-es';
import { isFreeUser, Role } from 'zl-shared';

import { AppState } from '../helper';

export const selectIsNonFreeHabitat = memoize((habitatId: string | undefined) =>
  habitatId
    ? createSelector(
        (state: AppState) => state.user.role,
        (state: AppState) => state.user.subscription.active,
        (state: AppState) => state.user.subscription.freeHabitats,
        (role, active, freeHabitats) => {
          if (isFreeUser(role as Role, { active: active ?? false })) {
            return !freeHabitats?.includes(habitatId);
          }
          // non-free user can access all habitats, so he/she has no non-free habitat
          return false;
        },
      )
    : () => false,
);

export const createSortedHabitatsSelector = ({
  liveTalk: prioritizeLiveTalk = true,
  freeHabitats: prioritizeFreeHabitats = true,
} = {}) =>
  createSelector(
    (state: AppState) => state.allHabitats,
    (state: AppState) => (prioritizeFreeHabitats ? state.user.subscription : undefined),
    (allHabitats, subscription) => {
      const { active, freeHabitats } = subscription ?? {};
      return [...allHabitats].sort((a, b) => {
        // sort by liveTalk status
        if (prioritizeLiveTalk && a.liveTalk !== b.liveTalk) {
          return b.liveTalk ? 1 : -1;
        }
        // if user is not subscriber, prioritize freeHabitats
        if (prioritizeFreeHabitats && !active && freeHabitats) {
          const aIsFree = freeHabitats.includes(a._id);
          const bIsFree = freeHabitats.includes(b._id);
          if (aIsFree !== bIsFree) {
            return aIsFree ? -1 : 1;
          }
        }
        // sort by online status
        if (a.isStreamOn !== b.isStreamOn) {
          return b.isStreamOn ? 1 : -1;
        }
        // sort by index ( considering index:0 as undefined )
        if ((a.index || Number.MAX_SAFE_INTEGER) !== (b.index || Number.MAX_SAFE_INTEGER)) {
          return (a.index || Number.MAX_SAFE_INTEGER) - (b.index || Number.MAX_SAFE_INTEGER);
        }
        // sort by currentViewers or alphabetically
        return b.currentViewers - a.currentViewers || a.title.localeCompare(b.title);
      });
    },
  );
